import slugify from "@sindresorhus/slugify";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {
    RelatedCategories,
    type DibDocument,
    type DibObjectRelated,
} from "../types";
import PATHS, {TOPIC_TYPE_PATHS} from "../../../paths";
import {isValidFileType} from "../contentUtils";
import {RelatedFileLink} from "../components/file/RelatedFileLink";
import classes from "./RelatedContentLEGACY.module.css";

const buildURL = (item: DibObjectRelated): string => {
    const topicTypeId = item.data.topictypeId;
    const slug = slugify(item.data.name);
    const id = item.data.dataResourceId;
    if (topicTypeId in TOPIC_TYPE_PATHS) {
        const key = Number(topicTypeId) as keyof typeof TOPIC_TYPE_PATHS;
        return `${PATHS.ROOT}${TOPIC_TYPE_PATHS[key]}/${slug}/${id}`;
    }
    return `${PATHS.ROOT}${PATHS.NOT_IMPLEMENTED}/${slug}/${id}`;
};

const Section = ({
    title,
    colorClass,
    items,
}: {
    title: string;
    colorClass: string;
    items: DibObjectRelated[];
}) => {
    if (items.length === 0) return null;
    return (
        <div className={classes.group}>
            <h3 className={`${classes.groupHeading} ${colorClass}`}>{title}</h3>
            <ul className={classes.itemList}>
                {items.map((item) => (
                    <li key={item.id}>
                        {isValidFileType(item.data.resourceTypeId) ? (
                            <RelatedFileLink
                                name={item.data.name}
                                resourceTypeId={item.data.resourceTypeId}
                                resourceId={item.data.dataResourceId}
                                variant="row"
                                className={classes.itemLink}
                            />
                        ) : (
                            <Link
                                to={buildURL(item)}
                                className={classes.itemLink}
                            >
                                {item.data.name}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const RelatedContentLEGACY = ({
    document: doc,
}: {
    document: DibDocument;
}) => {
    const {t} = useTranslation();
    const buckets: {
        index: DibObjectRelated[];
        tools: DibObjectRelated[];
        sources: DibObjectRelated[];
    } = {index: [], tools: [], sources: []};

    (doc.relatedroot ?? []).forEach((id) => {
        const obj = doc.objects[id];
        if (obj?.type !== "related") return;
        if (obj.data.categoryId === RelatedCategories.INDEX)
            buckets.index.push(obj);
        else if (obj.data.categoryId === RelatedCategories.TOOLS)
            buckets.tools.push(obj);
        else if (obj.data.categoryId === RelatedCategories.SOURCES)
            buckets.sources.push(obj);
    });

    const empty =
        buckets.index.length + buckets.tools.length + buckets.sources.length ===
        0;
    if (empty) return null;

    return (
        <section className={classes.related}>
            <h2 className={classes.heading}>
                {t("Title.RelatedContent", {
                    defaultValue: "relatert",
                })?.toLowerCase()}
            </h2>
            <Section
                title={t("Navigation.Lookup", {defaultValue: "Oppslag"})}
                colorClass={classes.index}
                items={buckets.index}
            />
            <Section
                title={t("topicSidebar.ToolsHeading")}
                colorClass={classes.tools}
                items={buckets.tools}
            />
            <Section
                title={t("Word.Sources", {defaultValue: "Kilder"})}
                colorClass={classes.sources}
                items={buckets.sources}
            />
        </section>
    );
};
